import { useNavigate } from 'react-router-dom';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { ScrollArea } from '../../ui/scroll-area';
import { 
  BookOpen,
  CheckCircle2,
  Circle,
  Target,
  MessageSquare,
  Mic
} from 'lucide-react';

/**
 * Sidebar with learning objectives, sections list, playbook link, and assistant
 */
export const LessonSidebar = ({ 
  lesson,
  topic,
  currentSectionIndex,
  completedSections,
  onSectionClick,
  onOpenAssistant
}) => {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      {/* Learning Objectives */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="h-5 w-5 text-cyan-600" />
            Learning Objectives
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {lesson?.learning_objectives?.map((obj, i) => (
              <li key={`obj-${i}`} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span>{obj}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Section List */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-lg">Sections</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-64">
            {lesson?.sections?.map((section, index) => {
              const isComplete = completedSections.includes(section.id);
              const isCurrent = index === currentSectionIndex;
              
              return (
                <div
                  key={section.id}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                    isCurrent ? 'bg-cyan-50 border-l-2 border-cyan-600' : 'hover:bg-muted/50'
                  }`}
                  onClick={() => onSectionClick(index)}
                  data-testid={`section-${index}`}
                >
                  {isComplete ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <Circle className={`h-5 w-5 flex-shrink-0 ${isCurrent ? 'text-cyan-600' : 'text-muted-foreground'}`} />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isCurrent ? 'text-cyan-700' : ''}`}>
                      {section.title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {section.duration_minutes} min
                    </p>
                  </div>
                </div>
              );
            })}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Playbook Link */}
      {topic?.playbook_id && (
        <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-violet-600" />
              <div className="flex-1">
                <p className="font-medium">Reference Playbook</p>
                <p className="text-sm text-muted-foreground">Deep dive materials</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate(`/rpat/playbook/${topic.playbook_id}`)}
              >
                View
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Assistant */}
      <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
        <CardContent className="pt-4">
          <div className="flex items-center gap-3">
            <MessageSquare className="h-8 w-8 text-cyan-600" />
            <div className="flex-1">
              <p className="font-medium">R-PAT Assistant</p>
              <p className="text-sm text-muted-foreground">Ask questions anytime</p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={onOpenAssistant}
              data-testid="rpat-assistant-btn"
            >
              <Mic className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LessonSidebar;
